import React from "react";
import { Box, Grid, IconButton, Skeleton, Typography } from "@mui/material";
import DetailRow from "../../pages/common-features/DetailRow";
import { useSelector } from "react-redux";
import { RootState } from "../../services/redux/store";
import { camelToTitle } from "../ui/StepperDyanmicForm/helper";
import VerifiedIcon from "../../assets/svgIcons/check-circle.svg";
import idIcon from "../../assets/svgIcons/id.svg";
interface StudentDetailsTabProps {
  studentDetails: any[];
  isMobile: boolean;
}
//  const AdhaarVerifyFiled = ({ isDocumentVerified }: any) => {
//     return <>
//       <Grid container className="inner-form-box" ml={2} mt={2}
//         sx={{
//           borderRadius: "8px",
//           paddingTop: "12px",
//           backgroundColor: "#ebf9f9",
//           paddingRight: "12px",
//           paddingBottom: "12px",
//           paddingLeft: "12px",
//           marginLeft: "28px"
//         }}
//       >
//           <Grid item xs={12} sm={12} md={12}>
//             <Box sx={{ display: "flex", gap: 1 }}>
//               <img src={idIcon} alt="icon" style={{ width: 20, height: 20 }} />

//               <Typography
//                 sx={{
//                   fontSize: "13px",
//                   fontWeight: 500,
//                   color: "#4B465C",
//                 }}
//               >
//                 Verify Aadhaar Number
//               </Typography>
//             </Box>

//             <label className="text-lable" style={{ marginBottom: "10px" }}>
//               Verify your Aadhaar through Digilocker to keep your application
//               going smoothly
//             </label>
//           </Grid>

//           <Grid item xs={12} sm={12} md={12}>
//             <IconButton
//                 sx={{
//                   color: isDocumentVerified ? "#33c2c4" : "#d58050",
//                   border: isDocumentVerified ? "1px solid #33c2c4" : "1px solid #d58050",
//                   "&.Mui-disabled": {
//                     backgroundColor: isDocumentVerified ? "white !important" : "transparent !important",
//                     color: isDocumentVerified ? "#33c2c4" : "#d58050",
//                     borderRadius: "6px",
//                     paddingRight: "16px",
//                     paddingLeft: "16px",
//                     fontWeight: 600,
//                     fontSize: "11px",
//                   },
//                 }}
//                 disabled={true}
//               >
//                 <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
//                   {isDocumentVerified && <img src={VerifiedIcon} alt="arrow-narrow-right" />}
//                   {isDocumentVerified ? "Verified" : "Not Verified"}
//                 </Box>

//             </IconButton>
//           </Grid>
//         </Grid>
//     </>
//   }

const StudentDetailsTab: React.FC<StudentDetailsTabProps> = ({
  studentDetails,
  isMobile,
}) => {
  const { fetchUserApplicationData, horizontalSteps } = useSelector(
    (state: RootState) => state.stepper
  );

  const normalizeFieldKey = (name: any) =>
    String(name || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  // Saved-value field names (from fetchUserApplicationData) carry a
  // "studentDetails" prefix the config names don't — e.g.
  // studentDetailsSalutation vs salutation — strip it so both shapes
  // normalize to the same key.
  const studentDetailsKeyOf = (field: any) =>
    normalizeFieldKey(field?.name).replace(/^studentdetails?/, "");

  // The fixed display order for Student Details, independent of whatever
  // order the backend's saved-values or field-config APIs happen to return
  // fields in.
  const STUDENT_DETAILS_DISPLAY_ORDER = [
    "salutation",
    "firstname",
    "lastname",
    "phonenumber",
    "email",
    "dob",
    "pan",
    "gender",
    "fatherfirstname",
    "fatherlastname",
    "motherfirstname",
    "motherlastname",
  ];
  const displayOrderIndex = new Map<string, number>(
    STUDENT_DETAILS_DISPLAY_ORDER.map((key, index) => [key, index]),
  );

  // Fields not covered by the fixed order above (e.g. a field added later
  // that isn't one of the standard ones) fall back to the field CONFIG's
  // `fieldOrder` — state.stepper.horizontalSteps names steps via `stepKey`/
  // `stepLabel` (fetchUserApplicationData, the saved-values shape, uses
  // `horizontalStepName`/`verticalStepName` instead — checked too, in case
  // the shape passed in ever matches that instead).
  const studentDetailsFieldOrder = new Map<string, number>();
  const applicantLoanConfigStep = (horizontalSteps || []).find(
    (h: any) =>
      h?.stepKey === "applicant_loan" ||
      h?.stepLabel === "Applicant & Loan Details" ||
      h?.horizontalStepName === "Applicant & Loan Details",
  );
  const studentDetailsConfigStep = applicantLoanConfigStep?.verticalSteps?.find(
    (v: any) =>
      v?.stepLabel === "Student Details" ||
      v?.verticalStepName === "Student Details",
  );
  (studentDetailsConfigStep?.fields || []).forEach((f: any) => {
    if (!f?.name) return;
    studentDetailsFieldOrder.set(normalizeFieldKey(f.name), Number(f.fieldOrder));
  });

  const sortByConfiguredFieldOrder = (fieldsToSort: any[]) => {
    const orderFor = (field: any) => {
      const key = studentDetailsKeyOf(field);
      const fixedIndex = displayOrderIndex.get(key);
      if (fixedIndex !== undefined) return fixedIndex;

      // Anything outside the fixed list sorts after it, ordered by the
      // field config (if available), so it doesn't get sorted mid-list.
      const configOrder = studentDetailsFieldOrder.get(key);
      return STUDENT_DETAILS_DISPLAY_ORDER.length +
        (configOrder === undefined || Number.isNaN(configOrder)
          ? Number.MAX_SAFE_INTEGER
          : configOrder);
    };
    return [...fieldsToSort].sort((a, b) => orderFor(a) - orderFor(b));
  };

  // Helper to map fields from the vertical step to the required format
  function mapStudentDetailsFromVerticalStep(verticalStep: any) {
    if (!verticalStep || !verticalStep?.fields) return [];
    return verticalStep?.fields.map((fieldObj: any) => {
      const name = Object.keys(fieldObj)?.[0];
      let value = fieldObj?.[name];
      // Format DOB as DD-MM-YYYY
      if (
        name.toLowerCase().includes("dob") &&
        value &&
        /^\d{4}-\d{2}-\d{2}$/.test(value)
      ) {
        const [year, month, day] = value.split("-");
        value = `${day}-${month}-${year}`;
      }
      // Remove 'studentDetails' or 'studentDetail' prefix and format label
      let label = name.replace(/^studentDetails?/i, "");
      // Special case for phone_number
      if (/phone_number/i.test(label)) {
        label = "Phone Number";
      } else {
        // Convert camelCase/snake_case to Title Case
        label = label
          .replace(/_/g, " ")
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (s) => s.toUpperCase())
          .trim();
      }
      return {
        label,
        name,
        value,
      };
    });
  }

  // Always try to extract student details from Redux if available
  let studentDetailsToShow = studentDetails;

  if (
    fetchUserApplicationData &&
    fetchUserApplicationData?.partnerStartedJourney &&
    studentDetailsToShow?.length === 0     
  ) {
    studentDetailsToShow = studentDetails || [];
  }

  if (
    fetchUserApplicationData &&
    !fetchUserApplicationData?.partnerStartedJourney
  ) {
    const applicantLoanStep = fetchUserApplicationData?.horizontalSteps?.find(
      (h: any) => h.horizontalStepName === "Applicant & Loan Details"
    );
    // Find the 'Student Details' vertical step
    const studentDetailsStep = applicantLoanStep?.verticalSteps?.find(
      (v: any) => v.verticalStepName === "Student Details"
    );
    if (studentDetailsStep) {
      studentDetailsToShow = sortByConfiguredFieldOrder(
        mapStudentDetailsFromVerticalStep(studentDetailsStep),
      );
    }
  }
  // These fields are shown under "Applicant Additional Details" instead,
  // so hide them here when the partner has started the journey.
  if (fetchUserApplicationData?.partnerStartedJourney) {
    const excludedPartnerFieldLabels = [
      "number of dependents",
      "maritial status",
      "marital status",
      "country of birth",
      "city of birth",
      "nationality",
    ];
    studentDetailsToShow = (studentDetailsToShow || []).filter((field: any) => {
      const label = String(field?.label || "").trim().toLowerCase();
      return !excludedPartnerFieldLabels.includes(label);
    });
  }

  // Whichever branch above populated it (the `studentDetails` prop as-is, or
  // the raw fetchUserApplicationData mapping), re-apply the configured field
  // order as a final pass so the displayed order always matches the form's
  // fieldOrder rather than whatever order the source data happened to be in.
  studentDetailsToShow = sortByConfiguredFieldOrder(studentDetailsToShow || []);

  if (!studentDetailsToShow || studentDetailsToShow.length === 0) {
    return (
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary">
          No details available.
        </Typography>
      </Grid>
    );
  }

  const formatDOB = (value: number | string) => {
    if (!value) return "-";

    const date = new Date(Number(value));
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const getVerificationStatuses = () => {
    // 1. Find the horizontal step
    const eligibilityStep = (fetchUserApplicationData?.horizontalSteps || []).find(
      (h: any) => h.horizontalStepName === "Eligibility Check"
    );
    const applicantLoanDetails = (fetchUserApplicationData?.horizontalSteps || []).find(
      (h: any) => h.horizontalStepName === "Applicant & Loan Details"
    );

    // 2. Find the specific vertical step by its name
    const targetVerticalStep = (eligibilityStep?.verticalSteps || []).find(
      (v:any) => v.verticalStepName?.trim() === "Verify Yourself and Provide Consent"
    );
    const targetApplicantLoanDetails = (applicantLoanDetails?.verticalSteps || []).find(
      (v:any) => v.verticalStepName?.trim() === "Student Details"
    );

    const fields = targetVerticalStep?.fields || [];

    // 3. Find the respective objects containing the keys
    const emailVerifiedObj = fields?.find((field: any) => field && 'isEmailVerified' in field);
    const mobileVerifiedObj = fields?.find((field: any) => field && 'isMobileVerified' in field);
    const adhaarVerifiedObj = targetApplicantLoanDetails?.fields?.find((field: any) => field && 'isAadhaarVerified' in field);
    // 4. Return both values (converting 'true' strings into actual booleans)
    return {
      isEmailVerified: emailVerifiedObj ? emailVerifiedObj.isEmailVerified === 'true' : false,
      isMobileVerified: mobileVerifiedObj ? mobileVerifiedObj.isMobileVerified === 'true' : false,
      isAadhaarVerified: adhaarVerifiedObj ? adhaarVerifiedObj?.isAadhaarVerified === "true" : false
    };
  };

  return (
    <>
      {studentDetailsToShow?.map((field: any, subIndex: number) => {
        const isMobileVerifiedField = studentDetailsToShow?.find(
          (f: any) => f.name === "isMobileVerified"
        );
        const isEmailVerifiedField = studentDetailsToShow?.find(
          (f: any) => f.name === "isEmailVerified"
        );
        const fieldName = field?.name?.trim().toLowerCase();

        const isPanVerifiedField = studentDetailsToShow?.find(
          (f: any) => f.name === "isPanVerified"
        );
        const isPanAvailabilityField = studentDetailsToShow?.find(
          (f: any) => String(f?.name || "").trim().toLowerCase() === "ispan"
        );
        const hasPanValue =
          String(isPanVerifiedField?.value).toLowerCase() === "true" &&
          String(isPanAvailabilityField?.value ?? "").trim().toLowerCase() !==
            "no";
        const isPanField = fieldName === "pan";

        const skipNames = [
          "aadhaar",
          "ispanverified",
          "isaadhaarverified",
          "ispan",
          "ismobileverified",
          "isemailverified",
        ];
        if (skipNames.includes(field?.name.toLowerCase())) return null;

        // const isVerified =
        //   (isMobileVerified) ||
        //   (isEmailVerified) ||
        //   hasPhoneValue ||
        //   hasEmailValue ||
        //   (isPanField && hasPanValue);

        let isVerified = false;
        let val = getVerificationStatuses();
        // Match by substring instead of an exact field-name string: when
        // partnerStartedJourney is true, studentDetailsToShow comes straight
        // from the Application&LoanDetails.StudentDetails API response
        // as-is (see the branch above), whose phone/email keys don't
        // necessarily match the "studentDetailsPhone_number" /
        // "studentDetailEmail" names the non-partner branch produces via
        // mapStudentDetailsFromVerticalStep — so the old exact-match checks
        // silently never matched for partner journeys, leaving phone/email
        // permanently unverified in the UI regardless of actual status.
        const isPhoneField = !!fieldName && fieldName.includes("phone");
        const isEmailField = !!fieldName && fieldName.includes("email");
        if (isPhoneField && val?.isMobileVerified === true) {
          isVerified = true;
        } else if (isEmailField && val?.isEmailVerified === true) {
          isVerified = true;
        } else if(isPanField && hasPanValue) {
          isVerified = true;
        }
        return (
          <Grid item xs={12} sm={6} md={3} key={subIndex}>
            <DetailRow
              item={{
                label: field?.label,
                value:
                  fetchUserApplicationData?.partnerStartedJourney &&
                  field?.name === "dob"
                    ? formatDOB(field?.value)
                    : field?.value,
                isVerified,
              }}
            />
          </Grid>
        );
      })}
      {/* <AdhaarVerifyFiled isDocumentVerified={getVerificationStatuses()?.isAadhaarVerified} /> */}
    </>
  );
};

export default StudentDetailsTab;